import React, { useState } from 'react';
import { ContactFormData } from '../../types/types';
import { sendContactForm } from '../../services/api';

export default function ContactUs() {
  const [formData, setFormData] = useState<ContactFormData>({
    email: '',
    subject: '',
    type: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);

    try {
      await sendContactForm(formData);
      setSucceeded(true);
      setFormData({ email: '', subject: '', type: '', message: '' });
    } catch (err: any) {
      setErrorMessage(err?.message || 'An unexpected error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  if (succeeded) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="bg-green-500 text-white py-3 px-6 rounded-lg shadow-lg max-w-lg mx-auto text-center transition-transform transform hover:scale-105 animate-fadeIn">
          <p className="font-semibold">
            Thank you — your message has been sent.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 p-8 rounded-lg shadow-lg max-w-4xl mx-auto">
      <form
        onSubmit={handleSubmit}
        className="space-y-6"
        aria-label="Contact form"
      >
        <div>
          <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Email address
          </label>
          <input
            type="email"
            name="email"
            aria-required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Subject
          </label>
          <input
            type="text"
            name="subject"
            aria-required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
            value={formData.subject}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Type
          </label>
          <select
            name="type"
            aria-required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
            value={formData.type}
            onChange={handleChange}
            required
          >
            <option value="" disabled hidden>
              Select a type
            </option>
            <option value="General Dataset Information">
              General Dataset Information
            </option>
            <option value="Downloading the Dataset">
              Downloading the Dataset
            </option>
            <option value="Benchmarks and Submissions">
              Benchmarks and Submissions
            </option>
            <option value="Bugs/Issues">Bugs/Issues</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Message
          </label>
          <textarea
            name="message"
            aria-required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-neutral-700 dark:text-white"
            rows={6}
            value={formData.message}
            onChange={handleChange}
            required
          />
        </div>

        {errorMessage && (
          <div className="text-red-600" role="alert">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition duration-200"
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? 'Sending...' : 'Send'}
        </button>
      </form>
    </div>
  );
}
